import { defineStore } from 'pinia'
import axios from 'axios'

const API_BASE_URL = 'http://localhost:8000'

export const useCompetitionStore = defineStore('competition', {
  state: () => ({
    competitions: [],
    currentCompetition: null,
    leaderboard: null,
    loading: false,
    error: null
  }),

  getters: {
    activeCompetitions: (state) => {
      return state.competitions.filter(comp => comp.is_active && !comp.is_locked)
    },
    
    lockedCompetitions: (state) => {
      return state.competitions.filter(comp => comp.is_locked)
    }
  },

  actions: {
    // Helper function to get auth headers
    getAuthHeaders() {
      const token = localStorage.getItem('auth_token')
      return token ? { Authorization: `Bearer ${token}` } : {}
    },

    async fetchCompetitions() {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`${API_BASE_URL}/competitions/`, {
          headers: this.getAuthHeaders()
        })
        this.competitions = response.data
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to fetch competitions'
        console.error('Error fetching competitions:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async createCompetition(competitionData) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post(`${API_BASE_URL}/competitions/`, competitionData, {
          headers: this.getAuthHeaders()
        })
        this.competitions.push(response.data)
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to create competition'
        console.error('Error creating competition:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async joinCompetition(competitionId, entryData) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post(`${API_BASE_URL}/competitions/${competitionId}/join`, entryData, {
          headers: this.getAuthHeaders()
        })
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to join competition'
        console.error('Error joining competition:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchCompetition(competitionId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`${API_BASE_URL}/competitions/${competitionId}`, {
          headers: this.getAuthHeaders()
        })
        this.currentCompetition = response.data
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to fetch competition'
        console.error('Error fetching competition:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchLeaderboard(competitionId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`${API_BASE_URL}/competitions/${competitionId}/leaderboard`, {
          headers: this.getAuthHeaders()
        })
        this.leaderboard = response.data
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to fetch leaderboard'
        console.error('Error fetching leaderboard:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchActiveCompetition(competitionId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`${API_BASE_URL}/competitions/${competitionId}/active`, {
          headers: this.getAuthHeaders()
        })
        this.leaderboard = response.data
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to fetch active competition data'
        console.error('Error fetching active competition data:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async deleteCompetition(competitionId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.delete(`${API_BASE_URL}/competitions/${competitionId}`, {
          headers: this.getAuthHeaders()
        })
        
        // Remove the competition from the local state
        this.competitions = this.competitions.filter(comp => comp.id !== competitionId)
        
        // Clear current competition if it was the deleted one
        if (this.currentCompetition && this.currentCompetition.id === competitionId) {
          this.currentCompetition = null
        }
        
        // Clear leaderboard if it was for the deleted competition
        if (this.leaderboard && this.leaderboard.competition_id === competitionId) {
          this.leaderboard = null
        }
        
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to delete competition'
        console.error('Error deleting competition:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async searchStocks(query, limit = 10) {
      try {
        const response = await axios.get(`${API_BASE_URL}/stocks/search`, {
          params: { query, limit },
          headers: this.getAuthHeaders()
        })
        return response.data
      } catch (error) {
        console.error('Error searching stocks:', error)
        throw error
      }
    },

    async validateStockSymbol(symbol) {
      try {
        const response = await axios.get(`${API_BASE_URL}/stocks/validate/${symbol}`, {
          headers: this.getAuthHeaders()
        })
        return response.data.is_valid
      } catch (error) {
        console.error('Error validating stock symbol:', error)
        return false
      }
    },

    async getStockPrice(symbol) {
      try {
        const response = await axios.get(`${API_BASE_URL}/stocks/price/${symbol}`, {
          headers: this.getAuthHeaders()
        })
        return response.data.price
      } catch (error) {
        console.error('Error getting stock price:', error)
        throw error
      }
    },

    // Moderation methods
    async fetchMyCompetitions() {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`${API_BASE_URL}/competitions/my-competitions`, {
          headers: this.getAuthHeaders()
        })
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to fetch my competitions'
        console.error('Error fetching my competitions:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchMyEntries() {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`${API_BASE_URL}/competitions/my-entries`, {
          headers: this.getAuthHeaders()
        })
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to fetch my entries'
        console.error('Error fetching my entries:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchPendingRequests(competitionId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`${API_BASE_URL}/competitions/${competitionId}/pending-requests`, {
          headers: this.getAuthHeaders()
        })
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to fetch pending requests'
        console.error('Error fetching pending requests:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async approveJoinRequest(competitionId, requestId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post(`${API_BASE_URL}/competitions/${competitionId}/requests/${requestId}/approve`, {}, {
          headers: this.getAuthHeaders()
        })
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to approve join request'
        console.error('Error approving join request:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async rejectJoinRequest(competitionId, requestId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post(`${API_BASE_URL}/competitions/${competitionId}/requests/${requestId}/reject`, {}, {
          headers: this.getAuthHeaders()
        })
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to reject join request'
        console.error('Error rejecting join request:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async removeEntry(competitionId, entryId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.delete(`${API_BASE_URL}/competitions/${competitionId}/entries/${entryId}`, {
          headers: this.getAuthHeaders()
        })
        
        // Refresh leaderboard to reflect the change
        if (this.leaderboard && this.leaderboard.competition_id === competitionId) {
          await this.fetchLeaderboard(competitionId)
        }
        
        return response.data
      } catch (error) {
        this.error = error.response?.data?.detail || 'Failed to remove entry'
        console.error('Error removing entry:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    clearError() {
      this.error = null
    }
  }
}) 